/**
 * Pure tile-geometry for the call surface (grid layout).
 *
 * Given the visible tile ids and the container size, returns an absolute rect
 * {x, y, width, height} per id. TileLayer renders ONE flat, id-keyed list and
 * positions each tile by these rects, so switching participant counts never
 * unmounts a tile -- <video> elements keep their srcObject and never re-attach.
 *
 * Ported from batesky-electron cfcalls tileGeometry.js, reduced to the grid
 * layout (which fits any N). The 7-mode layout system (solo/split/triangle/
 * quad/slots/zoom + filmstrip/pin) can be ported later if needed.
 */

const PAD = 16
const GAP = 16
const MAX_GRID_COLUMNS = 5

export interface TileRect {
  x: number
  y: number
  width: number
  height: number
}

/** Largest 16:9 tile that fits a cols x rows grid inside an inner area. */
function fit16x9(innerW: number, innerH: number, cols: number, rows: number) {
  if (innerW <= 0 || innerH <= 0) return {tileWidth: 0, tileHeight: 0}
  const availW = innerW - (cols - 1) * GAP
  const availH = innerH - (rows - 1) * GAP
  const wLimitedW = availW / cols
  const wLimitedH = (wLimitedW * 9) / 16
  if (wLimitedH * rows <= availH) {
    return {tileWidth: wLimitedW, tileHeight: wLimitedH}
  }
  const hLimitedH = availH / rows
  const hLimitedW = (hLimitedH * 16) / 9
  return {tileWidth: hLimitedW, tileHeight: hLimitedH}
}

/** Best column count maximizing total visible tile area. */
function bestGridColumns(width: number, height: number, n: number): number {
  if (!width || !height || !n) return 1
  const minColumns = n === 2 ? 2 : 1
  let bestCols = 1
  let bestArea = 0
  const innerW = width - 2 * PAD
  const innerH = height - 2 * PAD
  for (let c = minColumns; c <= Math.min(MAX_GRID_COLUMNS, n); c++) {
    const r = Math.ceil(n / c)
    const {tileWidth, tileHeight} = fit16x9(innerW, innerH, c, r)
    const area = tileWidth * tileHeight * n
    if (area > bestArea) {
      bestArea = area
      bestCols = c
    }
  }
  return bestCols
}

/**
 * Absolute rects for a centered grid of the given ids in the container.
 */
export function computeTileRects({
  visibleIds,
  width,
  height,
}: {
  visibleIds: string[]
  width: number
  height: number
}): Map<string, TileRect> {
  const rects = new Map<string, TileRect>()
  if (!width || !height || visibleIds.length === 0) return rects

  const cols = bestGridColumns(width, height, visibleIds.length)
  const rows = Math.ceil(visibleIds.length / cols)
  const innerW = width - 2 * PAD
  const innerH = height - 2 * PAD
  const {tileWidth, tileHeight} = fit16x9(innerW, innerH, cols, rows)
  if (tileWidth <= 0) return rects

  const usedRows = Math.ceil(visibleIds.length / cols)
  const fullBlockH = usedRows * tileHeight + (usedRows - 1) * GAP
  const originY = PAD + Math.max(0, (innerH - fullBlockH) / 2)

  visibleIds.forEach((id, i) => {
    const row = Math.floor(i / cols)
    const colCountThisRow = Math.min(cols, visibleIds.length - row * cols)
    const rowBlockW = colCountThisRow * tileWidth + (colCountThisRow - 1) * GAP
    const originX = PAD + Math.max(0, (innerW - rowBlockW) / 2)
    const col = i % cols
    rects.set(id, {
      x: originX + col * (tileWidth + GAP),
      y: originY + row * (tileHeight + GAP),
      width: tileWidth,
      height: tileHeight,
    })
  })

  return rects
}
